import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import cx from 'classnames';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { Toggler, TreeFilterGroup, TreeFilterItem } from 'plume-ui';

import { TogglerElementTypes } from 'plume-ui/dist/components/Toggler/Toggler';
import useWithConfirmation from '../../../../../utils/hooks/useWithConfirmation';
import FilterConfigWrapper from '../FilterConfigWrapper';
import {
  FilterPatternType,
  LocationAttributeFilter,
  LocationFilterType,
  Operator,
} from '../types';
import IncludeExcludeList from './IncludeExcludeList';
import LocationFilterItem from './LocationFilterItem';
import {
  getDefaultValueForFilter,
  getOperatorsForFilter,
  inputExistsForFilter,
} from './helpers';
import {
  LocationFilterCategory,
  LocationFilterConfigPayload,
  LocationFilterInitialData,
} from './types';

type LocationFilterConfigProps = {
  filterCategories: LocationFilterCategory[];
  initialData?: LocationFilterInitialData;
  initialOperator?: Operator;
  onChange?: (data: LocationFilterConfigPayload) => void;
  onChangeOperator?: (data: Operator) => void;
};

const LocationFilterConfig: FunctionComponent<LocationFilterConfigProps> = ({
  filterCategories,
  onChange,
  initialData,
  initialOperator,
  onChangeOperator,
}) => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState<string>();
  const [selectedItems, setSelectedItems] = useState<LocationFilterType[]>([]);
  const [isIncludeExclude, setIsIncludeExclude] = useState<boolean>(false);
  const [loadedInitialData, setLoadedInitialData] = useState<boolean>(false);
  const [selectedOperator, setSelectedOperator] = useState<Operator>(
    initialOperator || Operator.AND,
  );

  const withConfirmation = useWithConfirmation();

  const filteredItems = useMemo(() => {
    if (!filterCategories) return [];
    return filterCategories
      .flatMap((category) => category.filters)
      .filter(
        (item) =>
          inputExistsForFilter(item) &&
          item.attribute
            .toLowerCase()
            .includes((searchQuery || '').toLowerCase()),
      );
  }, [filterCategories, searchQuery]);

  const mapLocationFilterToTreeItem = (item: LocationAttributeFilter) => {
    return (
      <TreeFilterItem
        key={item.columnName}
        onClick={() => handleSelectItem(item)}
        info=""
        isChecked={
          selectedItems?.findIndex((it) => it.columnName === item.columnName) >=
          0
        }
        label={item.attribute}
        variant="default"
      />
    );
  };

  const mapLocationCategoryToTree = (category: LocationFilterCategory) => {
    const filtersWithExistingInputs = category.filters.filter((filter) =>
      inputExistsForFilter(filter),
    );
    if (!Boolean(filtersWithExistingInputs.length)) return null;
    return (
      <TreeFilterGroup key={`category-${category.name}`} title={category.name}>
        {filtersWithExistingInputs.map((filt) =>
          mapLocationFilterToTreeItem(filt),
        )}
      </TreeFilterGroup>
    );
  };

  const handleSelectItem = (item: LocationAttributeFilter) => {
    setIsIncludeExclude(false);
    const newItems = [
      ..._.cloneDeep(selectedItems),
      {
        ...item,
        columnValue: getDefaultValueForFilter(item),
        operator: item.pattern === FilterPatternType.Time ? 'now' : '=',
      },
    ];
    if (!_.isEqual(selectedItems, newItems))
      onChange?.({ data: newItems, isIncludeExclude: false });

    setSelectedItems(newItems);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = _.cloneDeep(selectedItems);
    newItems.splice(index, 1);
    if (!_.isEqual(selectedItems, newItems)) {
      onChange?.({ data: newItems, isIncludeExclude: false });
    }
    setSelectedItems(newItems);
  };

  const handleChangeItem = (newValue: any, index: number) => {
    const newItems = _.cloneDeep(selectedItems);
    newItems[index].columnValue = newValue;
    if (!_.isEqual(selectedItems, newItems))
      onChange?.({ data: newItems, isIncludeExclude: false });
    setSelectedItems(newItems);
  };

  const handleChangeItemOperator = (operator: string, index: number) => {
    const newItems = _.cloneDeep(selectedItems);
    newItems[index].operator = operator;
    if (!_.isEqual(selectedItems, newItems))
      onChange?.({ data: newItems, isIncludeExclude: false });
    setSelectedItems(newItems);
  };

  const handleSelectInclExcl = useCallback(() => {
    if (!isIncludeExclude) {
      if (selectedItems.length > 0) {
        withConfirmation({
          title: t('warning'),
          body: t('monitorAdd.includeExcludeListChangeWarning'),
          confirmButtonLabel: t('confirmation.confirm'),
          onConfirm: () => {
            setSelectedItems([]);
            setIsIncludeExclude(true);
            onChange?.({ list: undefined, isIncludeExclude: true });
          },
        });
      } else {
        setIsIncludeExclude(true);
        setSelectedItems([]);
        onChange?.({ list: undefined, isIncludeExclude: true });
      }
    } else {
      setIsIncludeExclude(false);
      onChange?.({ data: [], isIncludeExclude: false });
    }
  }, [isIncludeExclude, selectedItems]);

  // ! inital data (uncontrolled) should load into state only once
  useEffect(() => {
    if (initialData && !loadedInitialData) {
      if (initialData.isIncludeExclude) {
        setIsIncludeExclude(true);
        setSelectedItems([]);
      } else {
        setSelectedItems(_.cloneDeep(initialData.data));
      }
      setLoadedInitialData(true);
    }
  }, [initialData, loadedInitialData]);

  useEffect(() => {
    if (initialOperator) setSelectedOperator(initialOperator);
  }, [initialOperator]);

  const handleToggleSelect = (selected: TogglerElementTypes) => {
    const value = selected.key as Operator;
    setSelectedOperator(value);
    onChangeOperator?.(value);
  };

  return (
    <FilterConfigWrapper
      onSearch={setSearchQuery}
      noSelectionMessage={t('monitorAdd.pleaseSelectAttribute')}
      searchPlaceholder={t('monitorAdd.filterSearch.locations')}
      tableClassName={cx({
        'ConfigTable--includeExclude': isIncludeExclude,
      })}
      sectionTitle={t('monitorAdd.filterLocations.title')}
      selectedFilters={
        isIncludeExclude ? (
          <IncludeExcludeList
            selectedValue={
              initialData?.isIncludeExclude ? initialData.data : undefined
            }
            initialType={
              initialData?.isIncludeExclude ? initialData?.listType : undefined
            }
            onChange={(data) =>
              onChange?.({ list: data, isIncludeExclude: true })
            }
            onDelete={handleSelectInclExcl}
          />
        ) : (
          Boolean(selectedItems?.length) && (
            <>
              <div>
                <Toggler
                  value={selectedOperator}
                  onToggle={handleToggleSelect}
                  toggleElements={[
                    { label: 'AND', key: Operator.AND },
                    { label: 'OR', key: Operator.OR },
                  ]}
                />
              </div>
              {selectedItems?.map((item, i) => (
                <LocationFilterItem
                  key={`${item.columnName}-${i}`}
                  allowedOperators={getOperatorsForFilter(item)}
                  filter={item}
                  onChange={(data) => handleChangeItem(data, i)}
                  onChangeOperator={(operator) =>
                    handleChangeItemOperator(operator, i)
                  }
                  onDelete={() => handleDeleteItem(i)}
                />
              ))}
            </>
          )
        )
      }
      filterSelectorContent={
        <>
          {!Boolean(searchQuery?.length) && (
            <>
              {filterCategories.map((category) =>
                mapLocationCategoryToTree(category),
              )}
              <TreeFilterItem
                onClick={handleSelectInclExcl}
                isChecked={isIncludeExclude}
                label={t('monitors.includeExcludeLists')}
                info=""
              />
            </>
          )}
          {Boolean(searchQuery?.length) &&
            filteredItems.map((filter) => mapLocationFilterToTreeItem(filter))}
          {Boolean(searchQuery?.length) && !Boolean(filteredItems.length) && (
            <TreeFilterItem info="" label={t('noResults')} />
          )}
        </>
      }
    />
  );
};

export default memo(LocationFilterConfig);
